import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Eye, EyeOff, ShieldCheck, Sparkles, Trophy, Users, Rocket, CheckCircle2, XCircle } from 'lucide-react';
import { Button, Input, Card, CardContent } from '@nzassa/ui';
import { COLORS } from '@nzassa/shared';
import { useAuthStore } from '@/stores/authStore';
import { useSchoolStore } from '@/stores/schoolStore';
import { api } from '@/lib/api';
import { getDefaultRoute } from '@/lib/roleRedirect';
import { useAnalytics } from '@/hooks/useAnalytics';
import { usePasswordStrength } from '@/hooks/usePasswordStrength';
import { MicroSurveyModal } from '@/components/MicroSurveyModal';
import { SchoolBanner } from '@/components/SchoolBanner';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export function StudentLoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useDocumentTitle(t('auth.espace_apprenante'));
  const [searchParams] = useSearchParams();
  const login = useAuthStore((s) => s.login);
  const storeUser = useAuthStore((s) => s.user);
  const { trackEvent } = useAnalytics();
  const schoolCode = searchParams.get('ecole');
  const redirectTo = searchParams.get('redirect');
  const demoUser = searchParams.get('demoUser');
  const demoPassword = searchParams.get('demoPassword') ?? 'demo';
  const { school, fetchSchool } = useSchoolStore();

  useEffect(() => {
    if (schoolCode) fetchSchool(schoolCode);
  }, [schoolCode]);

  const [matricule, setMatricule] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [demoAttempted, setDemoAttempted] = useState(false);

  // First login state
  const [firstLogin, setFirstLogin] = useState(false);
  const [firstLoginMatricule, setFirstLoginMatricule] = useState('');
  const [firstLoginName, setFirstLoginName] = useState('');
  const [firstLoginOldPassword, setFirstLoginOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Survey & password strength
  const [showConfidenceSurvey, setShowConfidenceSurvey] = useState(false);
  const passwordStrength = usePasswordStrength(newPassword);

  // Live student count
  const [studentCount, setStudentCount] = useState<number | null>(null);
  useEffect(() => {
    api.get<{ users: number }>('/health')
      .then((d) => setStudentCount(d.users))
      .catch((e: unknown) => { if (import.meta.env.DEV) console.warn('[health]', e); });
  }, []);

  const canSubmit = matricule.trim().length >= 3 && password.length >= 1;

  const handleLogin = async (nextMatricule = matricule.trim(), nextPassword = password) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.login(nextMatricule, nextPassword);
      if (res.requiresFirstLogin) {
        setFirstLogin(true);
        setFirstLoginMatricule(res.matricule ?? nextMatricule);
        setFirstLoginName(res.userName ?? '');
        setFirstLoginOldPassword(nextPassword); // Save temp password for first-login verification
        return;
      }
      if (res.token && res.user) {
        login(res.token, res.user);
        trackEvent('student.login', { loginType: 'regular' });
        navigate(redirectTo || getDefaultRoute(res.user.role));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.erreur_generique'));
    } finally {
      setLoading(false);
    }
  };

  const handleFirstLogin = async () => {
    if (passwordStrength.strength === 'weak') {
      setError(t('auth.first_login.mdp_faible'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('auth.first_login.mdp_different'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.firstLogin(firstLoginMatricule, newPassword, confirmPassword, firstLoginOldPassword);
      if (res.token && res.user) {
        login(res.token, res.user);
        trackEvent('password.strength', { score: passwordStrength.score, strength: passwordStrength.strength });
        trackEvent('student.login', { loginType: 'first' });
        setShowConfidenceSurvey(true);
        return; // Don't navigate yet — show survey first
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.erreur_generique'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!demoUser || demoAttempted || loading) return;
    const normalizedDemoUser = demoUser.trim().toUpperCase();
    setDemoAttempted(true);
    setMatricule(normalizedDemoUser);
    setPassword(demoPassword);
    handleLogin(normalizedDemoUser, demoPassword);
  }, [demoAttempted, demoPassword, demoUser, loading]);

  // ── First Login Screen ──
  if (firstLogin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #0B3C5D 0%, #1a5276 50%, #0B3C5D 100%)' }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md"
        >
          <Card className="shadow-2xl border-0">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center bg-green-50">
                  <ShieldCheck className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">{t('auth.first_login.title')}</h2>
                <p className="text-sm text-slate-500 mt-1">
                  {t('auth.first_login.bienvenue')} <span className="font-semibold text-slate-700">{firstLoginName}</span> !
                </p>
                <p className="text-xs text-slate-400 mt-1">{t('auth.first_login.instructions')}</p>
              </div>

              <div className="text-xs text-slate-400 mb-4 text-center">
                {t('auth.matricule')} : <span className="font-mono font-semibold text-slate-600">{firstLoginMatricule}</span>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleFirstLogin(); }} className="space-y-4">
                <div>
                  <label htmlFor="student-new-password" className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">{t('auth.first_login.nouveau_mdp')}</label>
                  <div className="relative">
                    <Input id="student-new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} type={showPassword ? 'text' : 'password'} placeholder={t('auth.first_login.placeholder_mdp')} autoComplete="new-password" autoFocus />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? t('auth.masquer_mdp') : t('auth.afficher_mdp')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {/* Password Strength Meter */}
                {newPassword.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div
                          key={i}
                          className="h-1.5 flex-1 rounded-full transition-colors"
                          style={{
                            backgroundColor: i <= passwordStrength.score
                              ? passwordStrength.strength === 'weak' ? '#EF4444'
                                : passwordStrength.strength === 'medium' ? '#F59E0B'
                                : '#22C55E'
                              : '#E2E8F0',
                          }}
                        />
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      {[
                        { key: 'length' as const, labelKey: 'auth.password_criteria.length' },
                        { key: 'uppercase' as const, labelKey: 'auth.password_criteria.uppercase' },
                        { key: 'lowercase' as const, labelKey: 'auth.password_criteria.lowercase' },
                        { key: 'number' as const, labelKey: 'auth.password_criteria.number' },
                        { key: 'special' as const, labelKey: 'auth.password_criteria.special' },
                      ].map(({ key, labelKey }) => (
                        <div key={key} className="flex items-center gap-1 text-[10px]">
                          {passwordStrength.criteria[key]
                            ? <CheckCircle2 className="h-3 w-3 text-green-500" />
                            : <XCircle className="h-3 w-3 text-slate-300" />}
                          <span className={passwordStrength.criteria[key] ? 'text-green-600' : 'text-slate-400'}>{t(labelKey)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="student-confirm-password" className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">{t('auth.first_login.confirmer')}</label>
                  <Input id="student-confirm-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} type="password" autoComplete="new-password" placeholder={t('auth.first_login.retaper')} />
                </div>

                {error && <div role="alert" className="rounded-xl bg-red-50 border border-red-100 p-3 text-xs text-red-600">{error}</div>}

                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="ghost" onClick={() => { setFirstLogin(false); setError(''); }} className="flex-1">{t('common.retour')}</Button>
                  <Button type="submit" disabled={!newPassword || !confirmPassword || loading || passwordStrength.strength === 'weak'} variant="secondary" className="flex-[2]">
                    {loading ? t('auth.first_login.creation_en_cours') : t('auth.first_login.creer_mdp')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Confidence Pre Survey — non-dismissible after first login */}
        {showConfidenceSurvey && (
          <MicroSurveyModal
            surveyType="confidence_pre"
            dismissible={false}
            onClose={() => {
              setShowConfidenceSurvey(false);
              navigate(redirectTo || getDefaultRoute(storeUser?.role));
            }}
          />
        )}
      </div>
    );
  }

  // ── Main Login Screen (Gamified) ──
  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(135deg, #0B3C5D 0%, #1a5276 50%, #0B3C5D 100%)' }}>
      {/* School branding banner */}
      {school && <SchoolBanner schoolName={school.name} schoolLogoUrl={school.logoUrl} accentColor={school.accentColor} />}

      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-32 h-32 bg-white/5 rounded-full blur-2xl" />
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/3 rounded-full blur-3xl" />
      </div>

      <div className="flex-1 flex items-center justify-center p-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Logo + Title */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
              className="w-20 h-20 rounded-3xl mx-auto mb-4 flex items-center justify-center shadow-lg shadow-black/20"
              style={{ background: `linear-gradient(135deg, ${COLORS.orange}, #ff8c42)` }}
            >
              <img src="/logos/nzassalearn-compact.svg" alt="NzassaLearn" className="h-10 w-10" />
            </motion.div>
            <img src="/logos/nzassalearn-mono-white.svg" alt="NzassaLearn" className="h-7 mx-auto" />
            <p className="text-white/60 text-sm mt-1">{t('auth.espace_apprenante')}</p>
          </div>

          {/* Login Card */}
          <Card className="shadow-2xl border-0">
            <CardContent className="p-6 space-y-5">
              <div className="text-center">
                <h2 className="text-lg font-bold text-slate-900">{school?.welcomeMessage ?? t('auth.prete_a_apprendre')}</h2>
                <p className="text-xs text-slate-500 mt-1">{t('auth.connecte_toi')}</p>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); if (canSubmit) handleLogin(); }} className="space-y-4">
                <div>
                  <label htmlFor="student-matricule" className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">{t('auth.matricule')}</label>
                  <Input
                    id="student-matricule"
                    value={matricule}
                    onChange={(e) => setMatricule(e.target.value.toUpperCase())}
                    placeholder="Ex: ELEVE-001"
                    autoComplete="username"
                    autoFocus
                  />
                </div>

                <div>
                  <label htmlFor="student-password" className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">{t('auth.mot_de_passe')}</label>
                  <div className="relative">
                    <Input
                      id="student-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('auth.ton_mot_de_passe')}
                      autoComplete="current-password"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? t('auth.masquer_mdp') : t('auth.afficher_mdp')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1.5">
                    {t('auth.premiere_fois')}
                  </p>
                </div>

                {error && <div role="alert" className="rounded-xl bg-red-50 border border-red-100 p-3 text-xs text-red-600">{error}</div>}

                <Button
                  type="submit"
                  disabled={!canSubmit || loading}
                  variant="primary"
                  className="w-full h-12 text-base group"
                >
                  {loading ? t('auth.connexion_en_cours') : (
                    <>
                      <Rocket className="mr-2 h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                      {t('auth.cest_parti')}
                    </>
                  )}
                </Button>
              </form>

              {/* Info card */}
              <div className="rounded-2xl bg-blue-50/80 border border-blue-100 p-3 text-xs text-blue-700 flex items-start gap-2">
                <Sparkles className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{t('auth.info_matricule')}</span>
              </div>
            </CardContent>
          </Card>

          {/* Stats bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-6 flex justify-center gap-6"
          >
            {[
              { icon: Users, label: studentCount != null ? t('auth.eleves', { count: studentCount }) : '...', color: 'text-white/70' },
              { icon: Trophy, label: t('auth.modules'), color: 'text-white/70' },
            ].map(({ icon: Icon, label, color }) => (
              <div key={label} className={`flex items-center gap-1.5 text-xs ${color}`}>
                <Icon className="h-3.5 w-3.5" />
                <span>{label}</span>
              </div>
            ))}
          </motion.div>

          {/* Admin link */}
          <div className="mt-8 text-center">
            <button onClick={() => navigate('/admin-login')} className="text-xs text-white/30 hover:text-white/60 transition-colors">
              {t('auth.espace_admin')}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
